/**
 * CronJobs — Tüm APScheduler job'larını listeler + zamanlama düzenleme.
 *
 * Endpoint'ler (Haz 13, 2026):
 *   GET  /api/admin/cron-jobs                  → tüm job'ları listeler (override dahil)
 *   POST /api/admin/cron-jobs/{id}/run         → manuel tetikle (next_run değişmez)
 *   POST /api/admin/cron-jobs/{id}/pause       → duraklat (kalıcı)
 *   POST /api/admin/cron-jobs/{id}/resume      → devam ettir
 *   PUT  /api/admin/cron-jobs/{id}/schedule    → reschedule (interval/cron kalıcı)
 *   DELETE /api/admin/cron-jobs/{id}/schedule  → override'ı sil (varsayılan dön)
 */
import { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import {
  Clock, RefreshCw, Play, Loader2, Calendar,
  CheckCircle2, AlertCircle, Pause, Settings2, RotateCcw,
  History, ChevronDown, ChevronUp,
} from 'lucide-react';

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const diff = new Date(iso).getTime() - Date.now();
    const absSec = Math.abs(Math.round(diff / 1000));
    const sign = diff >= 0 ? 'sonra' : 'önce';
    if (absSec < 60) return `${absSec}sn ${sign}`;
    if (absSec < 3600) return `${Math.round(absSec / 60)}dk ${sign}`;
    if (absSec < 86400) return `${Math.round(absSec / 3600)}sa ${sign}`;
    return `${Math.round(absSec / 86400)}g ${sign}`;
  } catch { return ''; }
}

// Cron field "30" / "*/5" / "1,3" → human label (UI etiketleme için basit dönüşüm)
function fmtCronField(val) {
  if (val === null || val === undefined || val === '') return '*';
  return String(val);
}

const DAYS_OF_WEEK = [
  { val: 'mon', label: 'Pzt' }, { val: 'tue', label: 'Sal' },
  { val: 'wed', label: 'Çar' }, { val: 'thu', label: 'Per' },
  { val: 'fri', label: 'Cum' }, { val: 'sat', label: 'Cmt' },
  { val: 'sun', label: 'Paz' },
];

export default function CronJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [historyJobId, setHistoryJobId] = useState(null); // hangi job'ın geçmişi açık

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/cron-jobs');
      setJobs(res.data?.data?.jobs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cron listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const triggerNow = async (jobId, jobName) => {
    if (!window.confirm(`"${jobName}" şimdi çalıştırılsın mı? Arka planda başlatılır.`)) return;
    setRunning(jobId);
    try {
      await api.post(`/admin/cron-jobs/${jobId}/run`);
      toast.success(`${jobName} tetiklendi`);
      setTimeout(load, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tetikleme başarısız');
    } finally {
      setRunning(null);
    }
  };

  const togglePause = async (job) => {
    setToggling(job.id);
    try {
      const action = job.paused ? 'resume' : 'pause';
      await api.post(`/admin/cron-jobs/${job.id}/${action}`);
      toast.success(job.paused ? `${job.name} devam ediyor` : `${job.name} duraklatıldı`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="cron-jobs-page">
      <div className="flex items-center gap-3">
        <Clock className="h-6 w-6 text-indigo-500" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold">Cron / Zamanlı Görevler</h1>
          <p className="text-xs text-slate-500">
            Tüm arka plan periyodik işlerin durumu. Sıklığı veya saati değiştirebilir,
            duraklatıp devam ettirebilirsiniz. Değişiklikler kalıcıdır (restart-safe).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="cron-jobs-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {loading && jobs.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Yükleniyor…
        </Card>
      ) : jobs.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">Hiç aktif cron job yok.</Card>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <Card
              key={job.id}
              className={`p-3 hover:shadow-sm transition ${job.paused ? 'opacity-70 bg-slate-50 dark:bg-slate-900/40' : ''}`}
              data-testid={`cron-job-row-${job.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {job.paused
                    ? <Pause className="h-5 w-5 text-slate-400" />
                    : <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="font-semibold text-sm">{job.name}</div>
                    <code className="text-[10px] text-slate-500 font-mono">{job.id}</code>
                    {job.paused && (
                      <Badge className="bg-slate-200 text-slate-700 text-[10px]">Pasif</Badge>
                    )}
                    {job.override && (job.override.interval_minutes || job.override.cron) && (
                      <Badge className="bg-indigo-100 text-indigo-700 text-[10px]" title="Operatör değişikliği var">
                        Özelleştirildi
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                    {job.description}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px]">
                    <div className="flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
                      <Calendar className="h-3 w-3" />
                      <span className="font-medium">Zamanlama:</span>
                      <span className="font-mono">{job.trigger?.human || job.trigger?.expr}</span>
                    </div>
                    {job.next_run_time && !job.paused && (
                      <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                        <Clock className="h-3 w-3" />
                        <span className="font-medium">Sonraki:</span>
                        <span>{fmtDateTime(job.next_run_time)}</span>
                        <span className="text-slate-400">({relativeTime(job.next_run_time)})</span>
                      </div>
                    )}
                  </div>
                  {job.last_run && (
                    <div className="mt-1.5 text-[11px] flex items-center gap-1 flex-wrap">
                      {job.last_run.status === 'success' || job.last_run.status === 'success_manual' || job.last_run.status === 'completed'
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        : <AlertCircle className="h-3 w-3 text-rose-500" />}
                      <span className="text-slate-600 dark:text-slate-400">
                        Son: {fmtDateTime(job.last_run.at)} ·
                      </span>
                      <Badge
                        className={`text-[9px] px-1.5 py-0 ${
                          (job.last_run.status || '').startsWith('success') ? 'bg-emerald-100 text-emerald-700' :
                          job.last_run.status === 'missed' ? 'bg-amber-100 text-amber-700' :
                          job.last_run.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-rose-100 text-rose-700'
                        }`}
                      >
                        {job.last_run.status === 'success_manual' ? 'manuel ✓' :
                         job.last_run.status === 'error_manual' ? 'manuel ✗' :
                         job.last_run.status === 'missed' ? 'kaçırıldı' :
                         job.last_run.status}
                      </Badge>
                      {job.last_run.duration_ms !== undefined && job.last_run.duration_ms !== null && (
                        <span className="text-slate-400 text-[10px]">{job.last_run.duration_ms}ms</span>
                      )}
                      {job.last_run.details && (
                        <span className="text-rose-500 text-[10px] truncate">· {job.last_run.details}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500">{job.paused ? 'Pasif' : 'Aktif'}</span>
                    <Switch
                      checked={!job.paused}
                      onCheckedChange={() => togglePause(job)}
                      disabled={toggling === job.id}
                      data-testid={`cron-job-toggle-${job.id}`}
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setHistoryJobId(historyJobId === job.id ? null : job.id)}
                      data-testid={`cron-job-history-btn-${job.id}`}
                      title="Çalışma geçmişi"
                    >
                      {historyJobId === job.id ? <ChevronUp className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setEditJob(job)}
                      data-testid={`cron-job-edit-btn-${job.id}`}
                      title="Zamanlamayı düzenle"
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => triggerNow(job.id, job.name)}
                      disabled={running === job.id}
                      data-testid={`cron-job-run-btn-${job.id}`}
                    >
                      {running === job.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><Play className="h-3.5 w-3.5 mr-1" />Çalıştır</>}
                    </Button>
                  </div>
                </div>
              </div>
              {/* Expanded history panel */}
              {historyJobId === job.id && (
                <CronHistoryPanel jobId={job.id} />
              )}
            </Card>
          ))}
        </div>
      )}

      <Card className="p-3 bg-slate-50 dark:bg-slate-900/40 border-dashed">
        <div className="text-[11px] text-slate-600 dark:text-slate-400 space-y-0.5">
          <div>ℹ <b>Sayfa 30sn'de otomatik yenilenir.</b> Tüm değişiklikler restart-safe (DB'de saklanır).</div>
          <div><b>Aktif/Pasif:</b> Switch ile job'u duraklatın. <b>Çalıştır:</b> Sonraki normal çalışma zamanı değişmeden manuel tetikler.</div>
          <div><b>Düzenle (⚙):</b> Sıklık (dakika) veya gün+saat değiştirin. Varsayılana dönmek için modaldeki "Sıfırla" butonunu kullanın.</div>
        </div>
      </Card>

      {editJob && (
        <ScheduleEditModal
          job={editJob}
          onClose={() => setEditJob(null)}
          onSaved={async () => { setEditJob(null); await load(); }}
        />
      )}
    </div>
  );
}


function ScheduleEditModal({ job, onClose, onSaved }) {
  const isInterval = job.trigger_kind === 'IntervalTrigger';
  // Mevcut değerden başla — interval ise dakika, cron ise hour/minute/dow
  const currentInterval = (() => {
    if (!isInterval) return 5;
    const ov = job.override?.interval_minutes;
    if (ov) return ov;
    // expr "interval[0:05:00]" → 5 dakikayı parse et
    const m = (job.trigger?.expr || '').match(/(\d+):(\d+):(\d+)/);
    if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    return 5;
  })();
  const currentCron = (() => {
    if (isInterval) return { hour: 6, minute: 30, day_of_week: '' };
    const ov = job.override?.cron;
    if (ov) return {
      hour: ov.hour ?? 6, minute: ov.minute ?? 30,
      day_of_week: ov.day_of_week ?? '',
    };
    // expr "cron[hour='6', minute='30']" → parse
    const expr = job.trigger?.expr || '';
    const h = expr.match(/hour='?(\d+)'?/);
    const mn = expr.match(/minute='?(\d+)'?/);
    const dow = expr.match(/day_of_week='([^']*)'/);
    return {
      hour: h ? parseInt(h[1], 10) : 6,
      minute: mn ? parseInt(mn[1], 10) : 30,
      day_of_week: dow ? dow[1] : '',
    };
  })();
  const [interval, setInterval_] = useState(currentInterval);
  const [cron, setCron] = useState(currentCron);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = isInterval
        ? { interval_minutes: parseInt(interval, 10) }
        : { cron: {
            hour: parseInt(cron.hour, 10),
            minute: parseInt(cron.minute, 10),
            ...(cron.day_of_week ? { day_of_week: cron.day_of_week } : {}),
          } };
      const res = await api.put(`/admin/cron-jobs/${job.id}/schedule`, body);
      toast.success(`Zamanlama: ${res.data?.data?.trigger?.human || 'güncellendi'}`);
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Override silinecek, varsayılan zamanlama için backend restart gerekli. Devam?')) return;
    setResetting(true);
    try {
      await api.delete(`/admin/cron-jobs/${job.id}/schedule`);
      toast.success('Varsayılana sıfırlandı (kalıcı için restart gerekli)');
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sıfırlama başarısız');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cron-edit-modal">
        <DialogHeader>
          <DialogTitle>Zamanlamayı Düzenle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-slate-600 dark:text-slate-400 p-2 bg-slate-50 dark:bg-slate-900/40 rounded">
            <div><b>Job:</b> {job.name}</div>
            <div><b>Mevcut:</b> {job.trigger?.human}</div>
          </div>

          {isInterval ? (
            <div className="space-y-1.5">
              <Label htmlFor="interval">Sıklık (dakika)</Label>
              <Input
                id="interval"
                type="number" min="1" max="1440"
                value={interval}
                onChange={(e) => setInterval_(e.target.value)}
                data-testid="cron-edit-interval-input"
              />
              <div className="text-[10.5px] text-slate-500">
                1-1440 dakika arası (örn. 1=her dakika, 5=her 5dk, 60=her saat, 1440=günde 1)
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {[1, 5, 10, 15, 30, 60].map((v) => (
                  <Button
                    key={v} size="sm" variant="outline"
                    type="button"
                    onClick={() => setInterval_(v)}
                    className={`text-[10px] h-6 px-2 ${parseInt(interval, 10) === v ? 'bg-indigo-100' : ''}`}
                    data-testid={`cron-edit-interval-preset-${v}`}
                  >
                    {v}dk
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cron-hour">Saat (0-23)</Label>
                  <Input
                    id="cron-hour"
                    type="number" min="0" max="23"
                    value={cron.hour}
                    onChange={(e) => setCron({ ...cron, hour: e.target.value })}
                    data-testid="cron-edit-hour-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cron-minute">Dakika (0-59)</Label>
                  <Input
                    id="cron-minute"
                    type="number" min="0" max="59"
                    value={cron.minute}
                    onChange={(e) => setCron({ ...cron, minute: e.target.value })}
                    data-testid="cron-edit-minute-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Günler (boş bırakılırsa: her gün)</Label>
                <div className="flex flex-wrap gap-1">
                  {DAYS_OF_WEEK.map((d) => {
                    const selected = (cron.day_of_week || '').split(',').filter(Boolean);
                    const isOn = selected.includes(d.val);
                    return (
                      <Button
                        key={d.val} size="sm"
                        type="button"
                        variant={isOn ? 'default' : 'outline'}
                        onClick={() => {
                          const newSel = isOn
                            ? selected.filter((s) => s !== d.val)
                            : [...selected, d.val];
                          setCron({ ...cron, day_of_week: newSel.join(',') });
                        }}
                        className="text-[10px] h-7 px-2"
                        data-testid={`cron-edit-day-${d.val}`}
                      >
                        {d.label}
                      </Button>
                    );
                  })}
                  <Button
                    size="sm" variant="outline" type="button"
                    onClick={() => setCron({ ...cron, day_of_week: '' })}
                    className="text-[10px] h-7 px-2"
                  >
                    Her gün
                  </Button>
                </div>
              </div>
              <div className="text-[10.5px] text-slate-500">
                Önizleme: Her{cron.day_of_week ? ` ${cron.day_of_week.split(',').map(d => DAYS_OF_WEEK.find(x => x.val === d)?.label).join(', ')}` : ' gün'}{' '}
                saat <b>{fmtCronField(cron.hour).padStart(2, '0')}:{fmtCronField(cron.minute).padStart(2, '0')}</b>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          <Button
            type="button" variant="outline"
            onClick={reset} disabled={resetting || saving}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="cron-edit-reset-btn"
          >
            {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" />Varsayılana Sıfırla</>}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
            <Button
              type="button" onClick={save} disabled={saving || resetting}
              data-testid="cron-edit-save-btn"
            >
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Kaydediliyor</> : 'Kaydet'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Cron history panel ──────────────────────────────────────────────────
// Job kartının altında genişler. Son N çalışma + özet stats (başarı oranı,
// ortalama süre, kaçırılan vs.) + son hata mesajı.
function CronHistoryPanel({ jobId }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/cron-jobs/${jobId}/history?limit=30`);
      setHistory(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Geçmiş yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Geçmiş yükleniyor…
      </div>
    );
  }
  if (!history) return null;
  const { runs, stats } = history;

  const statusColor = (s) => {
    if (!s) return 'bg-slate-300';
    if (s.startsWith('success')) return 'bg-emerald-500';
    if (s === 'missed') return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2" data-testid={`cron-history-panel-${jobId}`}>
      {/* Stats row */}
      <div className="flex flex-wrap gap-3 text-[11px]">
        <div className="flex items-center gap-1">
          <span className="text-slate-500">Toplam:</span>
          <span className="font-semibold">{stats.total}</span>
        </div>
        {stats.success_rate !== null && (
          <div className="flex items-center gap-1">
            <span className="text-slate-500">Başarı:</span>
            <span className={`font-semibold ${stats.success_rate >= 95 ? 'text-emerald-600' : stats.success_rate >= 80 ? 'text-amber-600' : 'text-rose-600'}`}>
              %{stats.success_rate}
            </span>
            <span className="text-slate-400">({stats.success}/{stats.total})</span>
          </div>
        )}
        {stats.error > 0 && (
          <div className="flex items-center gap-1 text-rose-600">
            <AlertCircle className="h-3 w-3" />
            <span className="font-semibold">{stats.error} hata</span>
          </div>
        )}
        {stats.missed > 0 && (
          <div className="flex items-center gap-1 text-amber-600">
            <span className="font-semibold">{stats.missed} kaçırıldı</span>
          </div>
        )}
        {stats.avg_duration_ms !== null && (
          <div className="flex items-center gap-1">
            <span className="text-slate-500">Ortalama:</span>
            <span className="font-mono">{stats.avg_duration_ms}ms</span>
          </div>
        )}
        {stats.max_duration_ms !== null && stats.max_duration_ms !== stats.avg_duration_ms && (
          <div className="flex items-center gap-1">
            <span className="text-slate-500">Maks:</span>
            <span className="font-mono">{stats.max_duration_ms}ms</span>
          </div>
        )}
      </div>

      {/* Sparkline-style success/fail strip — last 30 runs left→right oldest→newest */}
      {runs.length > 0 && (
        <div className="flex items-center gap-0.5">
          {[...runs].reverse().map((r, idx) => (
            <div
              key={idx}
              className={`h-4 w-1.5 rounded-sm ${statusColor(r.status)}`}
              title={`${r.status} · ${fmtDateTime(r.started_at)}${r.duration_ms !== null ? ` · ${r.duration_ms}ms` : ''}${r.error_message ? `\n${r.error_message}` : ''}`}
            />
          ))}
          <span className="text-[10px] text-slate-400 ml-2">son {runs.length} çalışma</span>
        </div>
      )}

      {/* Son N run tablo */}
      <details className="text-[11px]">
        <summary className="cursor-pointer text-slate-600 dark:text-slate-400 hover:text-indigo-600">
          Detaylı liste ({runs.length})
        </summary>
        <div className="mt-2 max-h-64 overflow-y-auto border rounded">
          <table className="w-full text-[10.5px]">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
              <tr>
                <th className="text-left p-1.5">Zaman</th>
                <th className="text-left p-1.5">Durum</th>
                <th className="text-right p-1.5">Süre</th>
                <th className="text-left p-1.5">Detay</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                  <td className="p-1.5 font-mono">{fmtDateTime(r.started_at)}</td>
                  <td className="p-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${statusColor(r.status)} mr-1`}></span>
                    {r.status}
                  </td>
                  <td className="p-1.5 text-right font-mono">{r.duration_ms !== null ? `${r.duration_ms}ms` : '—'}</td>
                  <td className="p-1.5 text-rose-600 truncate max-w-xs" title={r.error_message || ''}>
                    {r.error_message || (r.manual_trigger_name ? `manuel: ${r.manual_trigger_name}` : '')}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan="4" className="p-3 text-center text-slate-400">Henüz çalışma kaydı yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

